import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import API from "../api.js";

const Signup = () => {
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [emailError, setEmailError] = useState("");
  const navigate = useNavigate();

  const validateEmail = (email) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });

    if (name === "email") {
      if (!validateEmail(value)) {
        setEmailError("⚠️ Please enter a valid email address");
      } else {
        setEmailError("");
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");

    if (!form.name.trim() || !form.email.trim() || !form.password.trim()) {
      setMessage("⚠️ All fields are required!");
      return;
    }

    if (!validateEmail(form.email)) {
      setEmailError("⚠️ Invalid email address!");
      return;
    }

    setLoading(true);

    try {
      const res = await API.post("/users/register", form);

      localStorage.setItem("email", form.email);

      const msg = res.data?.message || "";

      if (msg.includes("OTP sent") || msg.includes("already sent")) {
        setMessage("✅ OTP sent to your email! Please verify.");
        setIsRegistered(true);
        navigate("/verify");
        return;
      }

      setIsRegistered(true);
      setMessage(
        msg ||
          "✅ Registration successful! Please check your email for verification."
      );
      setForm({ name: "", email: "", password: "" });
    } catch (err) {
      const errorMsg =
        err.response?.data?.message ||
        "❌ Something went wrong! Please try again later.";
      setMessage(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-[#FAF6EF] to-[#E8DCCB]">
      <form
        onSubmit={handleSubmit}
        className="bg-white/90 backdrop-blur p-8 w-96
                   rounded-3xl
                   shadow-[0_20px_40px_rgba(0,0,0,0.15)]
                   transition-transform duration-300 hover:scale-[1.02]"
      >
        <h2
          className="text-3xl font-extrabold mb-4 text-center"
          style={{ color: "#3A2A1A" }}
        >
          Create Your Account
        </h2>

        {!isRegistered ? (
          <>
            {/* Name */}
            <input
              type="text"
              name="name"
              placeholder="Full Name"
              value={form.name}
              onChange={handleChange}
              className="w-full p-3 mb-4
                         border border-[#D8C7B0]
                         rounded-xl
                         focus:outline-none
                         focus:ring-2 focus:ring-[#C08A5A]"
              required
            />

            {/* Email */}
            <input
              type="email"
              name="email"
              placeholder="Email Address"
              value={form.email}
              onChange={handleChange}
              className={`w-full p-3 mb-2
                         border
                         rounded-xl
                         focus:outline-none
                         focus:ring-2 ${
                           emailError
                             ? "border-red-500 focus:ring-red-400"
                             : "border-[#D8C7B0] focus:ring-[#C08A5A]"
                         }`}
              required
            />
            {emailError && (
              <p className="text-red-600 text-sm mb-3">{emailError}</p>
            )}

            {/* Password */}
            <input
              type="password"
              name="password"
              placeholder="Password"
              value={form.password}
              onChange={handleChange}
              className="w-full p-3 mb-5
                         border border-[#D8C7B0]
                         rounded-xl
                         focus:outline-none
                         focus:ring-2 focus:ring-[#C08A5A]"
              required
            />

            {/* Button */}
            <button
              type="submit"
              disabled={loading}
              className={`w-full py-2.5 rounded-xl text-white font-semibold text-lg transition ${
                loading
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-[#8B5A2B] hover:bg-[#7A4A28]"
              }`}
            >
              {loading ? "Creating Account..." : "Sign Up"}
            </button>
          </>
        ) : (
          <div className="text-center">
            <h3 className="text-lg font-semibold text-green-700 mb-2">
              🎉 Registration Successful!
            </h3>
            <p className="text-[#5C4632] text-sm">
              Please verify your email before logging in.
            </p>
          </div>
        )}

        {message && (
          <p
            className={`mt-4 text-center font-medium ${
              message.includes("✅") || message.toLowerCase().includes("success")
                ? "text-green-700"
                : "text-red-600"
            }`}
          >
            {message}
          </p>
        )}
      </form>
    </div>
  );
};

export default Signup;
